import axios from 'axios';
import { trackAxiosError, trackErrorReport } from '../utils/errorTracker';

// Base API configuration
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3000',
  timeout: 300000, // 5 minutes — long-running grading/polling-friendly; submit endpoints may still override
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add token to headers if exists
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Log request untuk debugging canceled requests
    console.log(`🌐 API Request: ${config.method?.toUpperCase()} ${config.url}`, {
      timeout: config.timeout,
      timestamp: new Date().toISOString(),
    });
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    // Track business-level errors returned with HTTP 200 (status:error in body)
    const bodyStatus = response?.data?.status;
    if (bodyStatus === 'error') {
      const responseErrorId = response?.data?.['error-id'] || response?.data?.errorId || null;
      trackErrorReport({
        errorType: 'axios',
        message: response?.data?.message || 'Backend returned status:error with HTTP 200',
        code: response?.data?.code || 'http-200-status-error',
        errorId: responseErrorId,
        page: response?.config?.meta?.page || 'UnknownPage',
        action: response?.config?.meta?.action || 'api-request',
        practiceSessionId: response?.config?.meta?.practiceSessionId || null,
        http: {
          method: response?.config?.method?.toUpperCase(),
          url: response?.config?.url,
          statusCode: response?.status ?? 200,
          timeoutMs: response?.config?.timeout,
        },
      });
    }

    console.log(`✅ API Response: ${response.config.method?.toUpperCase()} ${response.config.url}`, {
      status: response.status,
      timestamp: new Date().toISOString(),
    });
    return response;
  },
  (error) => {
    const requestUrl = error?.config?.url || '';
    const shouldSkipErrorTracking =
      error?.config?.skipErrorTracking === true || requestUrl.includes('/error-report/submit');

    if (!shouldSkipErrorTracking) {
      trackAxiosError(error, {
        page: error?.config?.meta?.page,
        action: error?.config?.meta?.action,
        practiceSessionId: error?.config?.meta?.practiceSessionId,
        method: error?.config?.method?.toUpperCase(),
        url: error?.config?.url,
        timeoutMs: error?.config?.timeout,
      });
    }

    // Log error untuk debugging canceled requests
    const isCanceled = error.code === 'ECONNABORTED' || error.message?.includes('timeout') || error.message?.includes('canceled');
    if (isCanceled) {
      console.error(`❌ API Request CANCELED: ${error.config?.method?.toUpperCase()} ${error.config?.url}`, {
        code: error.code,
        message: error.message,
        timeout: error.config?.timeout,
        timestamp: new Date().toISOString(),
      });
    } else {
      console.error(`❌ API Error: ${error.config?.method?.toUpperCase()} ${error.config?.url}`, {
        status: error.response?.status,
        message: error.message,
        timestamp: new Date().toISOString(),
      });
    }
    
    // Handle error responses
    const errorMessage = error.response?.data?.message || error.message || '';
    const errorMessageLower = errorMessage.toLowerCase();
    const isLearnerIdError = errorMessageLower.includes('learner-id') || 
                             errorMessageLower.includes('learner id') ||
                             errorMessageLower.includes('learner-id is required') ||
                             errorMessageLower.includes('learner-id required') ||
                             errorMessageLower.includes('learner-id missing') ||
                             errorMessageLower.includes('must provide learner-id') ||
                             errorMessageLower.includes('learner-id and/or');
    
    // Redirect to login if 401 (Unauthorized) or 400 with learner-id error
    if (error.response?.status === 401 || (error.response?.status === 400 && isLearnerIdError)) {
      // Unauthorized or learner-id missing - clear auth data and redirect to login
      console.log('❌ Auth error detected, redirecting to login...', {
        status: error.response?.status,
        message: errorMessage,
        isLearnerIdError
      });
      localStorage.removeItem('token');
      localStorage.removeItem('userData');
      localStorage.removeItem('universal-learning-id');
      // Use window.location.href untuk force full page reload dan clear state
      window.location.href = '/#/learner/login';
    }
    return Promise.reject(error);
  }
);

export default api;

